/* eslint-disable react/function-component-definition */
/* eslint-disable no-unused-vars */
import React, { useMemo, Suspense } from 'react';
import { Row, Col, Skeleton } from 'antd';
import { useSelector } from 'react-redux';
import {
  Users,
  User,
  IdCard,
  AlertCircle,
  CheckCircle,
  Hourglass,
  XCircle,
  UserRoundPen,
  PencilRuler,
  BriefcaseBusiness
} from 'lucide-react';
import moment from 'moment';

import { CounterCard } from '../../common';
import { Main } from '../../styled';
import { PageHeader } from '../../../components/page-headers/page-headers';

const SaleRevenue = React.lazy(() => import('../../../container/dashboard/overview/demoTwo/SaleRevenue'));

const compAndEmpCounts = [
  {
    title: 'Total Employees',
    apiUrl: '/dash/total-emps',
    icon: User, // Single person for employees
    iconColor: '#2196f3', // Blue for total employees
    backgroundColor: '#e3f2fd',
    textColor: '#2196f3',
    textSize: '2rem',
    labelSize: '1rem',
  },
  {
    title: 'Active Employees',
    apiUrl: '/dash/total-emps-active',
    icon: CheckCircle, // CheckCircle for active employees
    iconColor: '#ffc107', // Yellow for active state
    backgroundColor: '#fff8e1',
    textColor: '#ffc107',
    textSize: '2rem',
    labelSize: '1rem',
  },
  {
    title: 'Inactive Employees',
    apiUrl: '/dash/total-emps-inactive',
    icon: XCircle, // XCircle for inactive employees
    iconColor: '#ff5722', // Orange for inactive state
    backgroundColor: '#fbe9e7',
    textColor: '#ff5722',
    textSize: '2rem',
    labelSize: '1rem',
  },
];
const cardCounts = [
  {
    title: 'Total Card Requests',
    apiUrl: '/dash/total-cards',
    icon: IdCard, // ID card icon for requests
    iconColor: '#4caf50',
    backgroundColor: '#e8f5e9',
    textColor: '#4caf50',
    textSize: '2rem',
    labelSize: '1rem',
  },
  {
    title: 'Cards Printed',
    apiUrl: '/dash/total-cards-printed',
    icon: CheckCircle, // CheckCircle for successful prints
    iconColor: '#2196f3',
    backgroundColor: '#e3f2fd',
    textColor: '#2196f3',
    textSize: '2rem',
    labelSize: '1rem',
  },
  {
    title: 'Cards In-Process',
    apiUrl: '/dash/total-cards-pending',
    icon: Hourglass, // Hourglass for cards close to expiry
    iconColor: '#ffc107',
    backgroundColor: '#fff8e1',
    textColor: '#ffc107',
    textSize: '2rem',
    labelSize: '1rem',
  },
  {
    title: 'Cards Rejected/Blocked',
    apiUrl: '/dash/total-cards-rejected',
    icon: XCircle, // XCircle for rejected cards
    iconColor: '#ff5722',
    backgroundColor: '#fbe9e7',
    textColor: '#ff5722',
    textSize: '2rem',
    labelSize: '1rem',
  },
];
const updateReqCounts = [
  {
    title: 'Total',
    apiUrl: '/dash/total-update-reqs',
    icon: UserRoundPen, // Group icon for total update requests
    iconColor: '#2196f3',
    backgroundColor: '#e3f2fd',
    textColor: '#2196f3',
    textSize: '2rem',
    labelSize: '1rem',
  },
  {
    title: 'Approved',
    apiUrl: '/dash/total-update-reqs-approved',
    icon: CheckCircle, // CheckCircle for approved updates
    iconColor: '#4caf50',
    backgroundColor: '#e8f5e9',
    textColor: '#4caf50',
    textSize: '2rem',
    labelSize: '1rem',
  },
  {
    title: 'Pending',
    apiUrl: '/dash/total-update-reqs-pending',
    icon: Hourglass, // Hourglass for pending updates
    iconColor: '#ffc107',
    backgroundColor: '#fff8e1',
    textColor: '#ffc107',
    textSize: '2rem',
    labelSize: '1rem',
  },
  {
    title: 'Rejected',
    apiUrl: '/dash/total-update-reqs-rejected',
    icon: XCircle, // XCircle for rejected updates
    iconColor: '#ff5722',
    backgroundColor: '#fbe9e7',
    textColor: '#ff5722',
    textSize: '2rem',
    labelSize: '1rem',
  },
];
const trainingCounts = [
  {
    title: 'Total',
    apiUrl: '/dash/total-training',
    icon: PencilRuler, // Companies are represented by a group
    iconColor: '#2196f3', // Blue for total employees
    backgroundColor: '#e3f2fd',
    textColor: '#2196f3',
    textSize: '2rem',
    labelSize: '1rem',
  },
  {
    title: 'In Process',
    apiUrl: '/dash/total-training-in-process',
    icon: BriefcaseBusiness, // CheckCircle for active employees
    iconColor: '#2196f3', // Blue for total employees
    backgroundColor: '#e3f2fd',
    textColor: '#2196f3',
    textSize: '2rem',
    labelSize: '1rem',
  },
  {
    title: 'Pending',
    apiUrl: '/dash/total-training-in-pending',
    icon: Hourglass, // Single person for employees
    iconColor: '#ffc107', // Yellow for active state
    backgroundColor: '#fff8e1',
    textColor: '#ffc107',
    textSize: '2rem',
    labelSize: '1rem',
  },
  {
    title: 'Completed',
    apiUrl: '/dash/total-training-in-completed',
    icon: CheckCircle, // XCircle for inactive employees
    iconColor: '#4caf50', // Green for total count
    backgroundColor: '#e8f5e9',
    textColor: '#4caf50',
    textSize: '2rem',
    labelSize: '1rem',
  },
];

const Dashboard = () => {

  const { authUser } = useSelector((state) => ({ authUser: state.auth.data }));

  return (
    <>
      <PageHeader className="ninjadash-page-header-main" title={`${authUser?.comp_name} Dashboard `} routes={[]} />
      <Main>
        <span>Company & Employees</span>
        <div style={{ padding: '20px space-y-5' }}>
          <Row gutter={[16, 16]}>
            {compAndEmpCounts.map((card, index) => (
              <Col xs={24} sm={12} md={8} lg={8} xl={8} key={index}>
                <CounterCard key={index + 1} {...card} />
              </Col>
            ))}
          </Row>
          <span>ID Cards</span>
          <Row gutter={[16, 16]} className='mt-2'>
            {cardCounts.map((card, index) => (
              <Col xs={24} sm={12} md={8} lg={8} xl={6} key={index}>
                <CounterCard key={index + 1} {...card} />
              </Col>
            ))}
          </Row>
          <span>Update Requests</span>
          <Row gutter={[16, 16]} className='mt-2'>
            {updateReqCounts.map((card, index) => (
              <Col xs={24} sm={12} md={8} lg={8} xl={6} key={index}>
                <CounterCard key={index + 1} {...card} />
              </Col>
            ))}
          </Row>
          <span>Training Requests</span>
          <Row gutter={[16, 16]} className='mt-2'>
            {trainingCounts.map((card, index) => (
              <Col xs={24} sm={12} md={8} lg={8} xl={6} key={index}>
                <CounterCard key={index + 1} {...card} />
              </Col>
            ))}
          </Row>
        </div>
        {/* <div className="mt-4" />
        <Suspense
          fallback={
            <div style={{ padding: '20px' }}>
              <Skeleton active />
            </div>
          }
        >
          <SaleRevenue title="Asad Ali" />
        </Suspense> */}
      </Main>
    </>
  );
};

export default Dashboard;
